import { useEffect, useState } from 'react';
import AdminLayout from '../../layouts/AdminLayout';
import Spinner from '../../components/common/Spinner';
import { getAuditLogs } from '../../api/admin.api';

const ACTION_LABELS = {
  EXAM_CREATED: 'Exam Created',
  EXAM_UPDATED: 'Exam Updated',
  EXAM_PUBLISHED: 'Exam Published/Unpublished',
  EXAM_DELETED: 'Exam Deleted',
  EXAM_CLONED: 'Exam Cloned',
  RESULT_OVERRIDDEN: 'Result Overridden',
  CANDIDATES_ASSIGNED: 'Candidates Assigned',
  CANDIDATE_INVITED: 'Candidate Invited',
  PASSWORD_RESET: 'Password Reset',
};

const AuditLog = () => {
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [filters, setFilters] = useState({ action: '', from: '', to: '' });
  const [expanded, setExpanded] = useState(null);
  const LIMIT = 50;

  const load = (p = page, f = filters) => {
    setLoading(true);
    getAuditLogs({ page: p, limit: LIMIT, ...(f.action && { action: f.action }), ...(f.from && { from: f.from }), ...(f.to && { to: f.to }) })
      .then((res) => { setLogs(res.data.rows); setTotal(res.data.total); })
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []); // eslint-disable-line

  const handleFilter = (e) => {
    e.preventDefault();
    setPage(1);
    load(1, filters);
  };

  const pages = Math.ceil(total / LIMIT);

  return (
    <AdminLayout>
      <div className="mb-7">
        <h1 className="text-2xl font-bold text-slate-900">Audit Log</h1>
        <p className="text-slate-500 text-sm mt-0.5">Track all admin actions across the platform.</p>
      </div>

      {/* Filters */}
      <form onSubmit={handleFilter} className="flex flex-wrap gap-3 mb-5">
        <select
          value={filters.action}
          onChange={(e) => setFilters({ ...filters, action: e.target.value })}
          className="border border-slate-200 rounded-xl px-3 py-2 text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-primary-500"
        >
          <option value="">All Actions</option>
          {Object.keys(ACTION_LABELS).map((k) => (
            <option key={k} value={k}>{ACTION_LABELS[k]}</option>
          ))}
        </select>
        <input type="date" value={filters.from} onChange={(e) => setFilters({ ...filters, from: e.target.value })}
          className="border border-slate-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500" />
        <input type="date" value={filters.to} onChange={(e) => setFilters({ ...filters, to: e.target.value })}
          className="border border-slate-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500" />
        <button type="submit" className="bg-primary-600 text-white px-4 py-2 rounded-xl text-sm font-semibold hover:bg-primary-700 transition-colors">
          Filter
        </button>
        <button type="button" onClick={() => { setFilters({ action: '', from: '', to: '' }); setPage(1); load(1, { action: '', from: '', to: '' }); }}
          className="border border-slate-200 text-slate-500 px-4 py-2 rounded-xl text-sm hover:border-slate-300 transition-colors">
          Clear
        </button>
      </form>

      {loading ? (
        <Spinner className="py-20" />
      ) : (
        <div className="bg-white rounded-2xl border border-slate-200 shadow-card overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-slate-50 border-b border-slate-200">
                <th className="px-4 py-3.5 text-left text-xs font-semibold text-slate-500 uppercase tracking-wide">Time</th>
                <th className="px-4 py-3.5 text-left text-xs font-semibold text-slate-500 uppercase tracking-wide">Actor</th>
                <th className="px-4 py-3.5 text-left text-xs font-semibold text-slate-500 uppercase tracking-wide">Action</th>
                <th className="px-4 py-3.5 text-left text-xs font-semibold text-slate-500 uppercase tracking-wide">Entity</th>
                <th className="px-4 py-3.5 text-left text-xs font-semibold text-slate-500 uppercase tracking-wide">Details</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {logs.length === 0 && (
                <tr>
                  <td colSpan={5} className="text-center py-14 text-slate-400">No audit logs found.</td>
                </tr>
              )}
              {logs.map((log) => (
                <>
                  <tr key={log.id} className="hover:bg-slate-50 transition-colors">
                    <td className="px-4 py-3 text-xs text-slate-400 whitespace-nowrap">
                      {new Date(log.created_at).toLocaleString()}
                    </td>
                    <td className="px-4 py-3 text-xs text-slate-600">{log.actor_email || '—'}</td>
                    <td className="px-4 py-3">
                      <span className="inline-flex items-center px-2 py-0.5 rounded-md bg-slate-100 text-slate-600 text-xs font-mono">
                        {log.action}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-xs text-slate-500">
                      {log.entity_type
                        ? log.entity_id != null
                          ? `${log.entity_type} #${log.entity_id}`
                          : log.entity_type
                        : '—'}
                    </td>
                    <td className="px-4 py-3">
                      {log.metadata && (
                        <button
                          onClick={() => setExpanded(expanded === log.id ? null : log.id)}
                          className="text-xs text-primary-600 hover:underline"
                        >
                          {expanded === log.id ? 'Hide' : 'Show'}
                        </button>
                      )}
                    </td>
                  </tr>
                  {expanded === log.id && (
                    <tr key={`${log.id}-meta`}>
                      <td colSpan={5} className="px-4 pb-3">
                        <div className="bg-slate-50 rounded-lg p-3 flex flex-wrap gap-x-6 gap-y-1.5">
                          {Object.entries(log.metadata || {}).map(([key, value]) => (
                            <div key={key} className="flex items-center gap-1.5 text-xs">
                              <span className="text-slate-400 capitalize">{key.replace(/_/g, ' ')}:</span>
                              <span className="font-medium text-slate-700">
                                {value === null ? '—' : typeof value === 'boolean' ? (value ? 'Yes' : 'No') : String(value)}
                              </span>
                            </div>
                          ))}
                        </div>
                      </td>
                    </tr>
                  )}
                </>
              ))}
            </tbody>
          </table>

          {/* Pagination */}
          {pages > 1 && (
            <div className="px-4 py-3 border-t border-slate-100 flex items-center justify-between">
              <p className="text-xs text-slate-400">{total} total entries</p>
              <div className="flex gap-1">
                <button
                  disabled={page === 1}
                  onClick={() => { const p = page - 1; setPage(p); load(p); }}
                  className="px-3 py-1.5 rounded-lg border border-slate-200 text-xs disabled:opacity-40 hover:border-slate-300"
                >
                  Prev
                </button>
                <span className="px-3 py-1.5 text-xs text-slate-500">{page} / {pages}</span>
                <button
                  disabled={page === pages}
                  onClick={() => { const p = page + 1; setPage(p); load(p); }}
                  className="px-3 py-1.5 rounded-lg border border-slate-200 text-xs disabled:opacity-40 hover:border-slate-300"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </AdminLayout>
  );
};

export default AuditLog;
